import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useId, useState, useEffect } from "react";
import type { FormEvent, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { ApiError, type AppWithAvailability, type JobSummary } from "@gwrctl/api-client";
import { getJob } from "@gwrctl/shared";
import { client } from "../lib/client.js";
import { humanizeError } from "../lib/errors.ts";
import { jobTitle, paramLabel, paramValue } from "../lib/labels.ts";
import { useToast } from "../lib/toast.tsx";
import { TRACK_LABELS, bumpSemver, fmtVersion, type BumpResult } from "../lib/release.ts";
import { Banner, Button, Check, Chips, Field, Modal } from "./Ui.tsx";

type BuildScript = NonNullable<AppWithAvailability["build"]>["script"];
type Params = Record<string, unknown>;
type Errors = Record<string, string>;

/** Formların doğru varsayılanı ve uyarıyı gösterebilmesi için sayfanın bildikleri. */
export interface LaunchContext {
  /** Bilinen güncel sürüm (son kontrol ya da yükseltmeden). */
  current?: { name?: string; code?: number };
  /** Son yükseltme henüz derlenmediyse. */
  pending?: BumpResult;
  /** Son başarılı derlemenin AAB yolu — Play yüklemesine önceden yazılır. */
  lastBuildPath?: string;
  /** Yayın hattından açıldı: derleme varsayılanı "üretim" olur (hat Play'e gider). */
  fromPipeline?: boolean;
  /** Son Play yüklemesinin track'i — aynı kanala yüklemek en olası niyet. */
  lastTrack?: string;
  /** Güncel runtime: sabit değer mi politika mı, registry'de sabitlenmiş mi. */
  runtime?: { value?: string; policy?: string; pinned?: string };
}

const TRACKS = ["internal", "alpha", "beta", "production"] as const;

/** Elle yazılmış formu olan işler; geri kalanı şemaya göre "parametresiz mi" diye denetlenir. */
const FORMS = new Set([
  "dev.server.start",
  "store.assets.preview",
  "store.assets.upload",
  "store.assets.generate",
  "build.gradle",
  "ota.publish",
  "ota.rollback",
  "web.rollback",
  "release.sync",
  "release.version.bump",
  "play.upload",
  "play.rollout.update",
]);

/**
 * İş başlatma diyaloğu.
 *
 * TOTP isteyen işlerde iki adım: önce parametreler, sonra ÖZET + kod. Özet adımı
 * bilinçli — yıkıcı bir işi tek tıkla başlatmak yerine ne göndereceğini bir kez
 * daha okutuyor.
 *
 * Parametre formları job tipine göre elle yazıldı; zod şemasından otomatik form
 * üretmek mümkün ama her işin doğru varsayılanı ve uyarısı farklı, otomatik form
 * bunu kaybederdi. Şema yine de son kapı: formu olmayan ama zorunlu alanı olan
 * bir iş boş parametreyle gönderilmez.
 */
export function JobLauncher({
  job,
  appId,
  appName,
  channels,
  buildScript,
  context = {},
  initialParams,
  onLaunched,
  onClose,
}: {
  job: JobSummary;
  appId: string;
  /** Başlıktaki görünen ad; yoksa kimlik. */
  appName?: string;
  channels: readonly string[];
  /** Uygulamanın kendi yayın betiği varsa derleme formu ona göre çizilir. */
  buildScript?: BuildScript;
  context?: LaunchContext;
  /** "Tekrar dene": başarısız işin parametreleri varsayılanların üstüne yazılır. */
  initialParams?: unknown;
  /** Verilirse iş sayfasına gidilmez; çağıran sayfa işi kendisi gösterir. */
  onLaunched?: (jobId: string) => void;
  onClose: () => void;
}): ReactNode {
  const navigate = useNavigate();
  const toast = useToast();
  const formId = useId();
  const [params, setParams] = useState<Params>(() => ({
    ...defaultParams(job.id, appId, channels, context, buildScript),
    ...(initialParams && typeof initialParams === "object" ? (initialParams as Params) : {}),
    app: appId,
  }));
  const [step, setStep] = useState<1 | 2>(1);
  const [totp, setTotp] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Alan hataları ilk gönderme denemesinden sonra görünür; açılır açılmaz kırmızı olmasın.
  const [attempted, setAttempted] = useState(false);

  const needsTotp = job.confirm === "totp";
  // Sunucu TOTP onayını oturuma 8 saat damgalıyor (girişte kod girildiyse de).
  // Pencere açıkken kod tekrar sorulmaz; süre dolmuşsa sunucu TOTP_REQUIRED der
  // ve kod alanı o an açılır.
  const queryClient = useQueryClient();
  const me = useQuery({ queryKey: ["me"], queryFn: () => client.me(), staleTime: 30_000 });
  const [forceCode, setForceCode] = useState(false);
  const askCode = needsTotp && (forceCode || !me.data?.totpFresh);
  const set = (key: string, value: unknown): void =>
    setParams((prev) => ({ ...prev, [key]: value }));

  const errors = validate(job.id, params);
  const hasForm = FORMS.has(job.id);
  const schemaIssues = schemaProblems(job.id, params);
  // Formu olmayan işte eksik alan varsa kullanıcı düzeltemez; gönderme hiç açılmaz.
  const unsupported = !hasForm && schemaIssues.length > 0;
  // Formlu işte elle doğrulamanın kaçırdığı şema hatası (ör. boş zorunlu not).
  const leftover = hasForm && Object.keys(errors).length === 0 ? schemaIssues : [];
  const blocked = unsupported || Object.keys(errors).length > 0 || leftover.length > 0;

  async function launch(code = totp): Promise<void> {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      if (askCode) {
        if (!/^\d{6}$/.test(code)) throw new Error("6 haneli doğrulama kodu gerekli");
        await client.confirm(code);
        // Yeni damga: sonraki işler 8 saat boyunca kod sormasın.
        void queryClient.invalidateQueries({ queryKey: ["me"] });
      }
      const res = await client.createJob({ type: job.id, app: appId, params });
      if (res.queuedOnly) {
        toast({
          tone: "info",
          title: "Kuyruğa alındı",
          body: res.message ?? "Agent bağlanınca çalışacak.",
          link: { to: `/jobs/${res.job.id}`, label: "Kaydı aç" },
        });
      }
      if (onLaunched) {
        onLaunched(res.job.id);
        onClose();
      } else {
        navigate(`/jobs/${res.job.id}`);
      }
    } catch (err) {
      if (err instanceof ApiError && err.needsTotp) {
        // Onay penceresi bu arada dolmuş: kodu şimdi iste.
        setForceCode(true);
        setStep(2);
        setError("Onay süresi dolmuş; doğrulama kodunu gir.");
        void queryClient.invalidateQueries({ queryKey: ["me"] });
      } else setError(humanizeError(err, "Başlatılamadı"));
      // Yanlış kod tekrar otomatik gönderilmesin; yeni kod yazılınca gider.
      if (askCode) setTotp("");
    } finally {
      setBusy(false);
    }
  }

  function submit(e: FormEvent): void {
    e.preventDefault();
    if (busy) return;
    if (step === 1) {
      setAttempted(true);
      if (blocked) return;
      if (needsTotp) {
        setError(null);
        setStep(2);
        return;
      }
    }
    void launch();
  }

  const shownErrors = attempted ? errors : {};

  return (
    <Modal
      title={jobTitle(job.id)}
      subtitle={
        <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <span>{appName ?? appId}</span>
          <span className="mono text-2xs text-neutral-600">{job.id}</span>
          <span className="text-neutral-600">
            {needsTotp ? `· adım ${step}/2` : job.estimate ? `· ${job.estimate}` : ""}
          </span>
        </span>
      }
      onClose={onClose}
      dismissable={!busy}
      footer={
        <>
          <Button onClick={step === 2 ? () => setStep(1) : onClose} disabled={busy}>
            {step === 2 ? "Geri" : "Vazgeç"}
          </Button>
          <Button
            type="submit"
            form={formId}
            variant="primary"
            loading={busy}
            disabled={unsupported || (attempted && blocked && step === 1)}
          >
            {needsTotp && step === 1 ? "Devam" : job.available ? "Başlat" : "Kuyruğa al"}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={submit} noValidate>
        {step === 1 ? (
          <div className="grid gap-3.5 p-4">
            {unsupported ? (
              <Banner tone="info">
                Bu işin panelde formu yok ve zorunlu alanları var (
                {schemaIssues.map(paramLabel).join(", ")}). Boş parametreyle göndermek yerine
                başlatılmıyor.
              </Banner>
            ) : (
              <ParamFields
                jobId={job.id}
                params={params}
                set={set}
                errors={shownErrors}
                channels={channels}
                buildScript={buildScript}
                context={context}
              />
            )}
            {attempted && leftover.length > 0 && (
              <p className="text-2xs text-danger">
                Eksik ya da geçersiz alan: {leftover.map(paramLabel).join(", ")}
              </p>
            )}
            {job.warning && <Banner tone="warn">{job.warning}</Banner>}
            {!job.available && (
              <p className="text-2xs text-neutral-500">
                Bu iş için gereken agent şu an bağlı değil — iş kuyruğa alınır ve agent
                bağlanınca çalışır.
              </p>
            )}
          </div>
        ) : (
          <div className="grid gap-3 p-4">
            <Banner tone="danger">{job.warning ?? "Bu işlem geri alınamaz."}</Banner>
            <dl className="grid gap-1.5">
              {Object.entries(params)
                .filter(([k, v]) => k !== "app" && v !== undefined)
                .map(([key, value]) => (
                  <div key={key} className="grid gap-x-3 text-sm sm:grid-cols-[140px_minmax(0,1fr)]">
                    <dt className="text-neutral-500">{paramLabel(key)}</dt>
                    <dd className="mono break-words text-neutral-100">{paramValue(key, value)}</dd>
                  </div>
                ))}
            </dl>
            {!askCode ? (
              <p className="text-2xs text-neutral-500">
                Bu oturumda son 8 saat içinde doğrulandın; kod gerekmiyor. Başlat'a basınca iş başlar.
              </p>
            ) : (
            <Field label="Doğrulama kodu" hint="6 hane girilince kendiliğinden gönderilir">
              <input
                className="input mono text-center text-md tracking-[0.25em]"
                value={totp}
                onChange={(e) => {
                  const code = e.target.value.replace(/\D/g, "").slice(0, 6);
                  setTotp(code);
                  if (code.length === 6) void launch(code);
                }}
                maxLength={6}
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="\d{6}"
                autoFocus
                disabled={busy}
              />
            </Field>
            )}
          </div>
        )}

        {error && (
          <p role="alert" className="px-4 pb-3 text-2xs text-danger">
            {error}
          </p>
        )}
      </form>
    </Modal>
  );
}

function defaultParams(
  jobId: string,
  appId: string,
  channels: readonly string[],
  context: LaunchContext = {},
  buildScript?: BuildScript,
): Params {
  const channel = channels.includes("production") ? "production" : (channels[0] ?? "production");
  switch (jobId) {
    case "dev.server.start":
      return { app: appId, mode: "web", clearCache: false };
    case "build.gradle":
      return {
        app: appId,
        task: buildScript?.task ?? "bundleRelease",
        clean: false,
        // Hattan gelen derleme Play'e gidecek; geliştirme ayarlarıyla paket almak
        // (ör. dev mağazası) en pahalı hata.
        production: Boolean(context.fromPipeline && buildScript?.productionArgs?.length),
      };
    case "ota.publish":
      // Beta varsa önce oraya: test telefonunda denenmeden herkese giden OTA en
      // pahalı hata. Production'a gönderim bilinçli bir seçim olsun.
      return { app: appId, channel: channels.includes("beta") ? "beta" : channel, message: "" };
    case "ota.rollback":
      return { app: appId, channel, updateGroupId: "" };
    case "web.rollback":
      return { app: appId, deploymentId: "" };
    case "release.sync":
      return { app: appId, channel, notes: { tr: "", en: "" }, rollbackWebOnOtaFailure: true };
    case "release.version.bump":
      return { app: appId, semver: "patch", bumpVersionCode: true, commit: true };
    case "store.assets.preview":
    case "store.assets.upload":
      return {
        app: appId,
        langs: [],
        phone: true,
        tablet: true,
        featureGraphic: true,
        replaceExisting: false,
        syncListingTexts: true,
      };
    case "store.assets.generate":
      return { app: appId, steps: "all", langs: [], rebuild: false };
    case "play.upload":
      return {
        app: appId,
        artifactId: context.lastBuildPath ?? "",
        track: isTrack(context.lastTrack) ? context.lastTrack : "alpha",
        rolloutPercent: 100,
        notes: { tr: "", en: "" },
      };
    case "play.rollout.update":
      // Oran değişikliği neredeyse her zaman üretimde; oran bilerek boş: yanlışlıkla %100 olmasın.
      return { app: appId, track: "production", rolloutPercent: undefined, halt: false };
    default:
      return { app: appId };
  }
}

function isTrack(v: unknown): v is (typeof TRACKS)[number] {
  return typeof v === "string" && (TRACKS as readonly string[]).includes(v);
}

const SEMVER = /^\d+\.\d+\.\d+$/;
/** Sunucudaki şemayla aynı: runtimeVersion için izin verilen biçim. */
const RUNTIME = /^[0-9A-Za-z][0-9A-Za-z.+-]{0,39}$/;

function validPercent(v: unknown): boolean {
  return typeof v === "number" && Number.isFinite(v) && v >= 0 && v <= 100;
}

/** Türkçe alan hataları; boşsa gönderilebilir. */
function validate(jobId: string, p: Params): Errors {
  const e: Errors = {};
  const blank = (k: string): boolean => typeof p[k] !== "string" || !(p[k] as string).trim();
  switch (jobId) {
    case "play.upload":
      if (blank("artifactId")) e.artifactId = "AAB yolu gerekli — önce derleme çalıştır ya da yolu yapıştır.";
      if (!validPercent(p.rolloutPercent)) e.rolloutPercent = "0 ile 100 arasında olmalı.";
      break;
    case "play.rollout.update":
      if (!validPercent(p.rolloutPercent)) e.rolloutPercent = "0 ile 100 arasında bir oran gir.";
      break;
    case "release.version.bump": {
      const semver = String(p.semver ?? "");
      const custom = !["patch", "minor", "major"].includes(semver);
      if (custom && !SEMVER.test(semver)) e.semver = "Sürüm adı x.y.z biçiminde olmalı (ör. 1.1.0).";
      if (custom && p.versionCode !== undefined) {
        const code = p.versionCode as number;
        if (!Number.isInteger(code) || code < 1) e.versionCode = "Pozitif tam sayı olmalı.";
      }
      if (p.runtimeVersion !== undefined && !RUNTIME.test(String(p.runtimeVersion))) {
        e.runtimeVersion = "Harf/rakamla başlamalı; yalnızca harf, rakam, nokta, + ve - (ör. 1.2.0).";
      }
      break;
    }
    case "ota.publish":
      if (blank("message")) e.message = "Yayın mesajı gerekli.";
      break;
    case "ota.rollback":
      if (blank("updateGroupId")) e.updateGroupId = "Geri dönülecek update grubunun kimliği gerekli.";
      break;
    case "web.rollback":
      if (blank("deploymentId")) e.deploymentId = "Geri dönülecek yayının kimliği gerekli.";
      break;
  }
  return e;
}

/** Şemanın reddettiği üst düzey alan adları (tekilleştirilmiş). */
function schemaProblems(jobId: string, params: Params): string[] {
  const def = getJob(jobId);
  if (!def) return [];
  const res = def.params.safeParse(params);
  if (res.success) return [];
  const keys = res.error.issues.map((i) => String(i.path[0] ?? "parametreler"));
  return [...new Set(keys)];
}

function ParamFields({
  jobId,
  params,
  set,
  errors,
  channels,
  buildScript,
  context = {},
}: {
  jobId: string;
  params: Params;
  set: (key: string, value: unknown) => void;
  errors: Errors;
  channels: readonly string[];
  buildScript?: BuildScript;
  context?: LaunchContext;
}): ReactNode {
  if (jobId === "dev.server.start") {
    return (
      <>
        <Field label="Mod">
          <Chips
            values={["web", "native", "backend"] as const}
            value={params.mode as string}
            onChange={(v) => set("mode", v)}
          />
        </Field>
        <Check
          label="Önbelleği temizle (--clear)"
          checked={params.clearCache as boolean}
          onChange={(v) => set("clearCache", v)}
        />
      </>
    );
  }

  if (jobId === "store.assets.preview" || jobId === "store.assets.upload") {
    return (
      <>
        <LangsField
          params={params}
          set={set}
          hint="Boş bırak → üretilmiş tüm diller. Virgülle ayır: tr, en, de"
        />
        <Check label="Telefon ekran görüntüleri" checked={params.phone as boolean} onChange={(v) => set("phone", v)} />
        <Check
          label="Tablet ekran görüntüleri (7 ve 10 inç)"
          checked={params.tablet as boolean}
          onChange={(v) => set("tablet", v)}
        />
        <Check
          label="Öne çıkan grafik"
          checked={params.featureGraphic as boolean}
          onChange={(v) => set("featureGraphic", v)}
        />
        <Check
          label="Play'de zaten olan görselleri de değiştir (kapalıyken elle yüklenenler korunur)"
          checked={params.replaceExisting as boolean}
          onChange={(v) => set("replaceExisting", v)}
        />
        <Check
          label="Mağaza metinlerini listing.json ile eşitle (başlık, kısa ve uzun açıklama)"
          checked={params.syncListingTexts as boolean}
          onChange={(v) => set("syncListingTexts", v)}
        />
        {jobId === "store.assets.upload" && (
          <p className="text-2xs text-neutral-500">
            Önce "{jobTitle("store.assets.preview")}" ile neyin değişeceğini görmen önerilir.
          </p>
        )}
      </>
    );
  }

  if (jobId === "store.assets.generate") {
    return (
      <>
        <Field
          label="Adımlar"
          hint="Tümü → çek + üret · Yalnızca çekim → ham ekranlar · Yalnızca tasarım → mevcut ekranlardan"
        >
          <Chips
            values={["all", "capture", "render"] as const}
            labels={{ all: "Tümü", capture: "Yalnızca çekim", render: "Yalnızca tasarım" }}
            value={params.steps as string}
            onChange={(v) => set("steps", v)}
          />
        </Field>
        <LangsField
          params={params}
          set={set}
          hint="Boş bırak → projedeki tüm diller. Virgülle ayır: tr, en, de"
        />
        <Check
          label="Web derlemesini zorla yenile (yeni commit yoksa da)"
          checked={params.rebuild as boolean}
          onChange={(v) => set("rebuild", v)}
        />
      </>
    );
  }

  if (jobId === "build.gradle" && buildScript) {
    const args = buildScript.productionArgs ?? [];
    return (
      <>
        <p className="text-xs text-neutral-400">
          Bu uygulama kendi yayın betiğiyle derlenir:{" "}
          <span className="mono break-all text-neutral-200">
            npm run {buildScript.name}
            {params.production && args.length ? ` -- ${args.join(" ")}` : ""}
          </span>
          . Çıktı Play'e yüklenecek AAB.
        </p>
        {args.length > 0 && (
          <Check
            label={`Üretim (${args.join(" ")})${buildScript.productionHint ? ` · ${buildScript.productionHint}` : ""}`}
            checked={params.production as boolean}
            onChange={(v) => set("production", v)}
          />
        )}
        {context.fromPipeline && args.length > 0 && !params.production && (
          <Banner tone="warn">Üretim kapalı: bu paket Play'e yüklenmek için uygun olmayabilir.</Banner>
        )}
      </>
    );
  }

  if (jobId === "build.gradle") {
    return (
      <>
        <Field label="Görev" hint="bundleRelease → Play için .aab · assembleRelease → .apk">
          <Chips
            values={["bundleRelease", "assembleRelease"] as const}
            value={params.task as string}
            onChange={(v) => set("task", v)}
          />
        </Field>
        <Check
          label="Önce clean (daha yavaş, daha güvenli)"
          checked={params.clean as boolean}
          onChange={(v) => set("clean", v)}
        />
      </>
    );
  }

  if (jobId === "ota.publish") {
    return (
      <>
        <ChannelField channels={channels} params={params} set={set} />
        <Field label="Mesaj · en fazla 200 karakter" error={errors.message}>
          <input
            className="input"
            maxLength={200}
            value={params.message as string}
            onChange={(e) => set("message", e.target.value)}
            placeholder="ör. afiş önbelleği düzeltmesi"
          />
        </Field>
      </>
    );
  }

  if (jobId === "ota.rollback") {
    return (
      <>
        <ChannelField channels={channels} params={params} set={set} />
        <Field
          label="Update grubu kimliği"
          hint="Yeniden yayınlanacak önceki update'in grup kimliği (EAS / OTA sunucusu listesinden)"
          error={errors.updateGroupId}
        >
          <input
            className="input mono"
            value={params.updateGroupId as string}
            onChange={(e) => set("updateGroupId", e.target.value.trim())}
            placeholder="ör. 3f6c1a2e-…"
          />
        </Field>
      </>
    );
  }

  if (jobId === "web.rollback") {
    return (
      <Field
        label="Yayın kimliği"
        hint="Geri dönülecek web yayınının kimliği (yayın işinin sonucunda yazar)"
        error={errors.deploymentId}
      >
        <input
          className="input mono"
          value={params.deploymentId as string}
          onChange={(e) => set("deploymentId", e.target.value.trim())}
        />
      </Field>
    );
  }

  if (jobId === "release.sync") {
    const notes = params.notes as { tr: string; en: string };
    return (
      <>
        <ChannelField channels={channels} params={params} set={set} />
        <Field label="Değişiklik notu (TR)">
          <textarea
            className="input"
            rows={2}
            value={notes.tr}
            onChange={(e) => set("notes", { ...notes, tr: e.target.value })}
          />
        </Field>
        <Field label="Changelog (EN)">
          <textarea
            className="input"
            rows={2}
            value={notes.en}
            onChange={(e) => set("notes", { ...notes, en: e.target.value })}
          />
        </Field>
        <Check
          label="OTA başarısız olursa web'i geri al"
          checked={params.rollbackWebOnOtaFailure as boolean}
          onChange={(v) => set("rollbackWebOnOtaFailure", v)}
        />
      </>
    );
  }

  if (jobId === "release.version.bump") {
    return <BumpFields params={params} set={set} errors={errors} context={context} />;
  }

  if (jobId === "play.upload") {
    const notes = (params.notes as { tr?: string; en?: string } | undefined) ?? {};
    return (
      <>
        <Field
          label="AAB yolu"
          hint={
            context.lastBuildPath && params.artifactId === context.lastBuildPath
              ? "Son başarılı derlemeden dolduruldu"
              : "Derleme işinin sonucundaki dosya yolu"
          }
          error={errors.artifactId}
        >
          <input
            className="input mono"
            value={params.artifactId as string}
            onChange={(e) => set("artifactId", e.target.value.trim())}
            placeholder="C:/…/movieport-2.6.1-27.aab"
          />
        </Field>
        <TrackField params={params} set={set} lastTrack={context.lastTrack} />
        <PercentField params={params} set={set} error={errors.rolloutPercent} label="Kademeli yayın %" />
        <Field label="Sürüm notu (TR) · isteğe bağlı">
          <textarea
            className="input"
            rows={2}
            value={notes.tr ?? ""}
            onChange={(e) => set("notes", { ...notes, tr: e.target.value })}
          />
        </Field>
        <Field label="Release notes (EN) · isteğe bağlı">
          <textarea
            className="input"
            rows={2}
            value={notes.en ?? ""}
            onChange={(e) => set("notes", { ...notes, en: e.target.value })}
          />
        </Field>
      </>
    );
  }

  if (jobId === "play.rollout.update") {
    return (
      <>
        <TrackField params={params} set={set} lastTrack={context.lastTrack} />
        <PercentField
          params={params}
          set={set}
          error={errors.rolloutPercent}
          label="Yeni yayın oranı %"
          hint="Kademeli yayının ulaşacağı kullanıcı yüzdesi"
        />
        <Check
          label="Yayını durdur (halt) — sorun görüldüyse yeni kullanıcılara dağıtım kesilir"
          checked={params.halt as boolean}
          onChange={(v) => set("halt", v)}
        />
      </>
    );
  }

  return <p className="text-xs text-neutral-500">Bu iş ek parametre istemiyor.</p>;
}

function ChannelField({
  channels,
  params,
  set,
}: {
  channels: readonly string[];
  params: Params;
  set: (key: string, value: unknown) => void;
}): ReactNode {
  return (
    <Field label="Kanal" hint={`Tanımlı kanallar: ${channels.join(", ")}`}>
      <Chips values={channels} value={params.channel as string} onChange={(v) => set("channel", v)} />
    </Field>
  );
}

function TrackField({
  params,
  set,
  lastTrack,
}: {
  params: Params;
  set: (key: string, value: unknown) => void;
  lastTrack?: string;
}): ReactNode {
  return (
    <Field
      label="Track"
      hint={
        lastTrack && isTrack(lastTrack)
          ? `Play Console'daki adlar · son yükleme: ${TRACK_LABELS[lastTrack]}`
          : "Play Console'daki adlar"
      }
    >
      <Chips
        values={TRACKS}
        labels={TRACK_LABELS}
        value={params.track as string}
        onChange={(v) => set("track", v)}
      />
    </Field>
  );
}

function PercentField({
  params,
  set,
  error,
  label,
  hint,
}: {
  params: Params;
  set: (key: string, value: unknown) => void;
  error?: string;
  label: string;
  hint?: string;
}): ReactNode {
  const value = params.rolloutPercent as number | undefined;
  return (
    <Field label={label} hint={hint} error={error}>
      <input
        className="input mono"
        type="number"
        inputMode="decimal"
        min={0}
        max={100}
        step="any"
        value={value ?? ""}
        // Boş kutu 0 sayılmasın: %0 geçerli bir değer ve "yayını durdur" demek.
        onChange={(e) => set("rolloutPercent", e.target.value === "" ? undefined : Number(e.target.value))}
      />
    </Field>
  );
}

function LangsField({
  params,
  set,
  hint,
}: {
  params: Params;
  set: (key: string, value: unknown) => void;
  hint: string;
}): ReactNode {
  const langs = (params.langs as string[] | undefined) ?? [];
  return (
    <Field label="Diller" hint={hint}>
      <input
        className="input mono"
        value={langs.join(", ")}
        onChange={(e) =>
          set(
            "langs",
            e.target.value
              .split(/[,\s]+/)
              .map((l) => l.trim())
              .filter(Boolean),
          )
        }
        placeholder="tümü"
      />
    </Field>
  );
}

/**
 * Sürüm yükseltme formu. Çalıştırmadan ÖNCE sonucu gösterir (1.2.0 · 5 → 1.2.1 · 6);
 * "özel" ile sürüm adı ve versionCode elle girilir — geri almak da bununla yapılır.
 */
function BumpFields({
  params,
  set,
  errors,
  context,
}: {
  params: Params;
  set: (key: string, value: unknown) => void;
  errors: Errors;
  context: LaunchContext;
}): ReactNode {
  const semver = params.semver as string;
  const custom = !["patch", "minor", "major"].includes(semver);
  const cur = context.current;
  const curName = cur?.name;
  const curCode = cur?.code;

  const nextName = custom
    ? semver
    : curName
      ? bumpSemver(curName, semver as "patch" | "minor" | "major")
      : undefined;
  const nextCode = custom
    ? (params.versionCode as number | undefined)
    : params.bumpVersionCode && curCode !== undefined
      ? curCode + 1
      : curCode;

  // Biçim hatası deneme beklemeden görünür: yazarken hemen fark edilsin.
  const badName = custom && !SEMVER.test(semver);
  const lowerCode = nextCode !== undefined && curCode !== undefined && nextCode < curCode;

  // runtimeVersion: undefined → dokunulmaz. "match" yeni sürüm adını izler.
  const rt = context.runtime;
  const curRuntime = rt?.value;
  const [runtimeMode, setRuntimeMode] = useState<"keep" | "match" | "custom">(
    params.runtimeVersion === undefined ? "keep" : "custom",
  );
  useEffect(() => {
    if (runtimeMode === "match" && nextName && SEMVER.test(nextName)) set("runtimeVersion", nextName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runtimeMode, nextName]);
  const nextRuntime = runtimeMode === "keep" ? curRuntime : (params.runtimeVersion as string | undefined);
  const runtimeChanges = runtimeMode !== "keep" && nextRuntime !== undefined && nextRuntime !== curRuntime;
  const pinConflict = runtimeChanges && rt?.pinned !== undefined && rt.pinned !== nextRuntime;
  const noChange = nextName === curName && nextCode === curCode && !runtimeChanges;

  return (
    <>
      {context.pending && (
        <Banner tone="warn">
          Son yükseltme ({fmtVersion(context.pending.version, context.pending.versionCode)}) henüz
          derlenmedi. Tekrar yükseltirsen bir numara boşa gider; sıradaki adım derleme.
        </Banner>
      )}
      <Field label="Sürüm artışı">
        <Chips
          values={["patch", "minor", "major", "özel"] as const}
          value={custom ? "özel" : semver}
          onChange={(v) => {
            if (v === "özel") {
              set("semver", curName ?? "1.0.0");
              set("versionCode", curCode ?? 1);
            } else {
              set("semver", v);
              set("versionCode", undefined);
            }
          }}
        />
      </Field>
      {custom ? (
        <div className="grid gap-3 sm:grid-cols-2">
          <Field
            label="Sürüm adı"
            hint="x.y.z"
            error={badName ? "Sürüm adı x.y.z biçiminde olmalı (ör. 1.1.0)." : errors.semver}
          >
            <input
              className="input mono"
              value={semver}
              onChange={(e) => set("semver", e.target.value.trim())}
            />
          </Field>
          <Field label="versionCode" error={errors.versionCode}>
            <input
              className="input mono"
              type="number"
              min={1}
              value={(params.versionCode as number | undefined) ?? ""}
              onChange={(e) => set("versionCode", e.target.value ? Number(e.target.value) : undefined)}
            />
          </Field>
        </div>
      ) : (
        <Check
          label="versionCode'u da artır"
          checked={params.bumpVersionCode as boolean}
          onChange={(v) => set("bumpVersionCode", v)}
        />
      )}
      <Field
        label="runtimeVersion"
        hint={
          rt?.policy
            ? `Şu an politika: ${rt.policy} — sürümle birlikte kendiliğinden değişiyor.`
            : rt?.pinned
              ? `Registry'de sabit: ${rt.pinned}. OTA yalnızca aynı runtime'daki kurulumlara gider.`
              : "OTA yalnızca aynı runtime'daki kurulumlara gider."
        }
        error={errors.runtimeVersion}
      >
        <Chips
          values={["keep", "match", "custom"] as const}
          labels={{
            keep: `Aynı kalsın${curRuntime ? ` (${curRuntime})` : rt?.policy ? " (politika)" : ""}`,
            match: "Yeni sürümle eşitle",
            custom: "Özel",
          }}
          value={runtimeMode}
          onChange={(v) => {
            setRuntimeMode(v);
            if (v === "keep") set("runtimeVersion", undefined);
            if (v === "custom") set("runtimeVersion", curRuntime ?? nextName ?? "");
          }}
        />
      </Field>
      {runtimeMode === "custom" && (
        <input
          className="input mono"
          aria-label="Özel runtimeVersion"
          value={(params.runtimeVersion as string | undefined) ?? ""}
          onChange={(e) => set("runtimeVersion", e.target.value.trim())}
          placeholder="ör. 1.2.0"
        />
      )}
      <div className="rounded-[6px] border border-neutral-800 px-3.5 py-2.5 text-sm">
        {cur ? (
          <span className="mono grid gap-1 break-words">
            <span>
              <span className="text-neutral-500">{fmtVersion(curName, curCode)}</span>
              <span className="text-neutral-600"> → </span>
              <span className="text-neutral-100">{fmtVersion(nextName, nextCode)}</span>
            </span>
            <span className="text-xs">
              <span className="text-neutral-600">runtime </span>
              <span className="text-neutral-500">{curRuntime ?? (rt?.policy ? `politika: ${rt.policy}` : "—")}</span>
              {runtimeChanges && (
                <>
                  <span className="text-neutral-600"> → </span>
                  <span className="text-warn">{nextRuntime}</span>
                </>
              )}
            </span>
          </span>
        ) : (
          <span className="text-neutral-500">
            Güncel sürüm bilinmiyor — önizleme için önce sürüm kontrolünü çalıştır.
          </span>
        )}
      </div>
      {lowerCode && (
        <p className="text-2xs text-warn">
          versionCode düşürülüyor. Yalnızca Play'e hiç yüklenmemiş bir numaraya dönerken güvenli;
          Play aynı numarayı ikinci kez kabul etmez.
        </p>
      )}
      {runtimeChanges && (
        <Banner tone="warn">
          runtime değişiyor: eski runtime'daki kurulumlar bundan sonraki OTA güncellemelerini
          almaz. Bu değerle yeni bir Play derlemesi yayınlaman gerekir.
        </Banner>
      )}
      {pinConflict && (
        <Banner tone="danger">
          Registry'de runtime {rt?.pinned} olarak sabitlenmiş. Değiştirirsen sürüm kontrolü
          hata verir; registry'deki sabitin de güncellenmesi gerekir.
        </Banner>
      )}
      {noChange && cur && <p className="text-2xs text-neutral-500">Sürüm değişmiyor.</p>}
      <Check label="Commit at" checked={params.commit as boolean} onChange={(v) => set("commit", v)} />
    </>
  );
}
